import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Trophy,
  Target,
  TrendingUp,
  Share2,
  Home,
  RotateCcw,
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Award,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/shared/Button';
import { examService } from '../services/exam.service';
import { resultsService, type ResultsWithReview } from '../services/results.service';

/** Map API exam type → Dutch section name */
const EXAM_TYPE_TO_SECTION: Record<string, string> = {
  reading: 'Lezen',
  listening: 'Luisteren',
  writing: 'Schrijven',
  speaking: 'Spreken',
  knm: 'KNM',
};

/** Map exam level → Program number */
const LEVEL_TO_PROGRAM: Record<string, string> = {
  B1: 'I',
  B2: 'II',
  A2: 'I',
};

interface SectionScore {
  name: string;
  score: number;
  maxScore: number;
  percentage: number;
}

interface PerformanceData {
  label: string;
  value: number;
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const ResultsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const attemptId = searchParams.get('attemptId');

  const [loading, setLoading] = useState(!!attemptId);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ResultsWithReview | null>(null);
  const [retryingGrading, setRetryingGrading] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  // Derive section & program from fetched exam data, with URL params as fallback
  const section = (data?.exam?.type && EXAM_TYPE_TO_SECTION[data.exam.type])
    || searchParams.get('section') || 'Lezen';
  const program = (data?.exam?.level && LEVEL_TO_PROGRAM[data.exam.level])
    || searchParams.get('program') || 'I';
  const isKnm = data?.exam?.type === 'knm';

  // Auth is enforced by ProtectedRoute

  useEffect(() => {
    if (!attemptId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    resultsService
      .getResultsWithReview(attemptId)
      .then((res) => {
        if (!cancelled) {
          setData(res);
          setLoading(false);
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || 'Could not load results.');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [attemptId]);

  useEffect(() => {
    if (!attemptId || !data || data.exam?.type !== 'speaking') return;
    if (data.gradingStatus === 'completed' || data.gradingStatus === 'not-required' || data.gradingStatus === 'failed') return;

    const timer = setInterval(() => {
      resultsService
        .getResultsWithReview(attemptId)
        .then((res) => setData(res))
        .catch(() => {});
    }, 4000);

    return () => clearInterval(timer);
  }, [attemptId, data]);

  const examTitle = data?.exam?.title;
  const examType = data?.exam?.type || 'reading';
  const isSpeaking = examType === 'speaking';
  const isWritingUngraded = examType === 'writing' && data?.percentage == null;
  const gradingStatus = data?.gradingStatus ?? (isSpeaking ? 'pending' : 'completed');
  const isGradingPending = (isWritingUngraded && gradingStatus !== 'failed') || (isSpeaking && (gradingStatus === 'pending' || gradingStatus === 'processing'));
  const isGradingFailed = (isSpeaking || isWritingUngraded) && gradingStatus === 'failed';
  const isGradingCompleted = !isWritingUngraded && (!isSpeaking || gradingStatus === 'completed' || gradingStatus === 'not-required');

  const overallScore = isGradingCompleted ? (data?.percentage ?? 0) : 0;
  const passed = isGradingCompleted ? data?.passed === true : false;
  const totalQuestions = data?.totalQuestions ?? data?.totalAnswers ?? 0;
  const correctAnswers = isSpeaking
    ? (isGradingCompleted ? data?.answers?.filter((a) => a.isCorrect === true).length ?? 0 : 0)
    : (data?.answers?.filter((a) => a.isCorrect).length ?? 0);
  const incorrectAnswers = totalQuestions - correctAnswers;
  const timeTaken = data?.duration != null ? formatDuration(data.duration) : '--:--';
  const averageTimePerQuestion =
    totalQuestions > 0 && data?.duration != null
      ? formatDuration(Math.floor(data.duration / totalQuestions))
      : '--:--';

  const sectionBreakdown: SectionScore[] = useMemo(() => {
    if (!data?.answers?.length) {
      return [{ name: EXAM_TYPE_TO_SECTION[examType] || examType, score: 0, maxScore: totalQuestions, percentage: 0 }];
    }
    if (examType === 'speaking') {
      return [{ name: 'Spreken', score: Math.round(data.percentage ?? 0), maxScore: 100, percentage: Math.round(data.percentage ?? 0) }];
    }
    const score = data.answers.reduce((sum, a) => sum + (a.points ?? 0), 0);
    const maxScore = data.answers.reduce((sum, a) => sum + (a.maxPoints ?? 1), 0);
    return [
      {
        name: EXAM_TYPE_TO_SECTION[examType] || examType,
        score: Math.round(score),
        maxScore,
        percentage: Math.round(data.percentage ?? 0),
      },
    ];
  }, [data?.answers, data?.percentage, examType, totalQuestions]);

  const performanceHistory: PerformanceData[] = []; // Optional: could fetch history later

  const tips = useMemo(() => {
    // KNM is a Dutch-society exam, not a language exam. The reading-level and vocabulary
    // tips below are meaningless for it, so KNM gets theme- and institution-based advice.
    if (isKnm) {
      return [
        t('results.tips.knmStudyThemes'),
        t('results.tips.knmInstitutions'),
        t('results.tips.knmPractice'),
      ];
    }
    const defaultTips = [
      t('results.tips.practiceDaily'),
      t('results.tips.useWordList'),
    ];
    if (sectionBreakdown.length > 0) {
      const weakest = sectionBreakdown[0];
      return [
        t('results.tips.focusOn', { section: weakest.name, percentage: weakest.percentage }),
        ...defaultTips,
      ];
    }
    return defaultTips;
  }, [sectionBreakdown, t, isKnm]);

  const handleReviewAnswers = () => {
    if (attemptId) navigate(`/answer-review?attemptId=${attemptId}`);
    else navigate('/answer-review');
  };

  const handleNewExam = () => {
    navigate('/dashboard');
  };

  const handleRetrySpeakingGrading = async () => {
    if (!attemptId || retryingGrading) return;
    setRetryingGrading(true);
    try {
      setRetryError(null);
      await examService.retrySpeakingGrading(attemptId);
      setData((prev) =>
        prev
          ? {
              ...prev,
              gradingStatus: 'processing',
              gradingError: undefined,
            }
          : prev,
      );
    } catch (err: any) {
      setRetryError(err?.response?.data?.message || t('results.gradingRetryFailed'));
    } finally {
      setRetryingGrading(false);
    }
  };

  const handleGradeWriting = async () => {
    if (!attemptId || retryingGrading) return;
    setRetryingGrading(true);
    setRetryError(null);
    try {
      await examService.gradeWritingAttempt(attemptId);
      setData(await resultsService.getResultsWithReview(attemptId));
    } catch (err: any) {
      setRetryError(err?.response?.data?.message || t('results.gradingRetryFailed'));
    } finally {
      setRetryingGrading(false);
    }
  };

  const handleShare = () => {
    const shareText = isKnm ? t('knm.shareMessage', { score: overallScore }) : t('results.shareMessage', { score: overallScore, section, program });
    if (navigator.share) {
      navigator.share({
        title: t('results.shareTitle'),
        text: shareText,
        url: window.location.href,
      }).catch(() => {});
    } else {
      alert(shareText);
    }
  };

  const chartWidth = 300;
  const chartHeight = 120;
  const padding = 20;
  const hasHistory = performanceHistory.length > 1;
  const maxValue = hasHistory ? Math.max(...performanceHistory.map((d) => d.value)) : 100;
  const minValue = hasHistory ? Math.min(...performanceHistory.map((d) => d.value)) - 10 : 0;
  const points = hasHistory
    ? performanceHistory.map((d, i) => {
        const x = padding + (i / (performanceHistory.length - 1)) * (chartWidth - 2 * padding);
        const y = chartHeight - padding - ((d.value - minValue) / (maxValue - minValue)) * (chartHeight - 2 * padding);
        return { x, y, ...d };
      })
    : [];
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
          <p className="text-gray-600">Loading results…</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md text-center">
          <p className="text-gray-800 font-medium mb-4">{error}</p>
          <Button variant="primary" onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }
  if (!attemptId || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md text-center">
          <p className="text-gray-800 font-medium mb-4">No results to show.</p>
          <Button variant="primary" onClick={() => navigate('/dashboard')}>Take an exam</Button>
          <Button variant="tertiary" className="mt-3" onClick={() => navigate('/dashboard')}>Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-amber-50 to-white">
      {/* Hero Section */}
      <div
        className={`py-12 ${
          isGradingPending
            ? 'bg-gradient-to-r from-blue-100 to-sky-100'
            : isGradingFailed
              ? 'bg-gradient-to-r from-warning/20 to-amber-100'
              : passed
                ? 'bg-gradient-to-r from-success/20 to-emerald-100'
                : 'bg-gradient-to-r from-error/20 to-red-100'
        }`}
      >
        <div className="max-w-4xl mx-auto px-4 text-center">
          {/* Trophy Icon */}
          <div
            className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 ${
              isGradingPending
                ? 'bg-blue-200'
                : isGradingFailed
                  ? 'bg-amber-200'
                  : passed
                    ? 'bg-success/20'
                    : 'bg-error/20'
            }`}
          >
            {isGradingPending ? (
              <Loader2 className="w-12 h-12 text-blue-700 animate-spin" />
            ) : isGradingFailed ? (
              <AlertTriangle className="w-12 h-12 text-amber-700" />
            ) : passed ? (
              <Trophy className="w-12 h-12 text-success" />
            ) : (
              <Target className="w-12 h-12 text-error" />
            )}
          </div>

          {/* Score */}
          <div className="mb-4">
            <div
              className={`text-7xl font-bold mb-2 ${
                isGradingPending
                  ? 'text-blue-700'
                  : isGradingFailed
                    ? 'text-amber-700'
                    : passed
                      ? 'text-success'
                      : 'text-error'
              }`}
            >
              {isGradingCompleted ? `${overallScore}%` : '--'}
            </div>
            <div
              className={`inline-flex items-center px-6 py-2 rounded-full text-lg font-bold ${
                isGradingPending
                  ? 'bg-blue-700 text-white'
                  : isGradingFailed
                    ? 'bg-amber-600 text-white'
                    : passed
                      ? 'bg-success text-white'
                      : 'bg-error text-white'
              }`}
            >
              {isGradingPending ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t(isWritingUngraded ? 'results.writingGradingPendingBadge' : 'results.speakingGradingPendingBadge')}
                </>
              ) : isGradingFailed ? (
                <>
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  {t(isWritingUngraded ? 'results.writingGradingFailedBadge' : 'results.speakingGradingFailedBadge')}
                </>
              ) : passed ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {t('results.passed')}
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 mr-2" />
                  {t('results.notPassed')}
                </>
              )}
            </div>
          </div>

          {/* Exam Info */}
          <p className="text-gray-600 mb-2">
            {examTitle || section} {t('common.exam')}
          </p>
          <p className="text-sm text-gray-500">
            {isGradingPending
              ? t(isWritingUngraded ? 'results.writingNotGraded' : 'results.speakingPending')
              : isGradingFailed
                ? isWritingUngraded ? t('results.writingGradingFailed') : data?.gradingError || t('results.gradingFailed')
                : t('results.correctAnswersShort', { correct: correctAnswers, total: totalQuestions })}
          </p>
        </div>
      </div>

      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Stats */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
                <div className="w-12 h-12 mx-auto bg-success/10 rounded-full flex items-center justify-center mb-2">
                  <CheckCircle className="w-6 h-6 text-success" />
                </div>
                <p className="text-2xl font-bold text-success">{correctAnswers}</p>
                <p className="text-xs text-gray-500">{t('results.quickStats.correct')}</p>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
                <div className="w-12 h-12 mx-auto bg-error/10 rounded-full flex items-center justify-center mb-2">
                  <XCircle className="w-6 h-6 text-error" />
                </div>
                <p className="text-2xl font-bold text-error">{incorrectAnswers}</p>
                <p className="text-xs text-gray-500">{t('results.quickStats.incorrect')}</p>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
                <div className="w-12 h-12 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-2">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <p className="text-2xl font-bold text-primary">{timeTaken}</p>
                <p className="text-xs text-gray-500">{t('results.quickStats.totalTime')}</p>
              </div>
            </div>

            {/* Section Breakdown */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-secondary" />
                {t('results.scorePerSection')}
              </h2>
              <div className="space-y-4">
                {isGradingPending && (
                  <p className="text-sm text-blue-700">{t('results.scorePending')}</p>
                )}
                {isGradingFailed && (
                  <p className="text-sm text-amber-700">{t('results.gradingFailedHint')}</p>
                )}
                {sectionBreakdown.map((sec, idx) => (
                  <div key={idx}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">{sec.name}</span>
                      <span className="text-sm font-bold text-gray-900">
                        {isGradingCompleted
                          ? `${sec.score}/${sec.maxScore} (${sec.percentage}%)`
                          : '--/-- (--%)'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 h-3 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          sec.percentage >= 70 ? 'bg-success' : sec.percentage >= 50 ? 'bg-secondary' : 'bg-error'
                        }`}
                        style={{ width: `${isGradingCompleted ? sec.percentage : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Performance Chart - only when we have history */}
            {hasHistory && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  {t('results.progressOverTime')}
                </h2>
                <div className="flex justify-center">
                  <svg width={chartWidth} height={chartHeight} className="overflow-visible">
                    {[50, 60, 70, 80, 90].map((v) => {
                      const y =
                        chartHeight -
                        padding -
                        ((v - minValue) / (maxValue - minValue)) *
                          (chartHeight - 2 * padding);
                      return (
                        <g key={v}>
                          <line x1={padding} y1={y} x2={chartWidth - padding} y2={y} stroke="#e5e7eb" strokeDasharray="4" />
                          <text x={padding - 5} y={y + 4} textAnchor="end" className="text-xs fill-gray-400">{v}%</text>
                        </g>
                      );
                    })}
                    <path d={pathD} fill="none" stroke="#F7931E" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    {points.map((p, i) => (
                      <g key={i}>
                        <circle cx={p.x} cy={p.y} r="6" fill="#F7931E" />
                        <circle cx={p.x} cy={p.y} r="3" fill="white" />
                        <text x={p.x} y={chartHeight - 5} textAnchor="middle" className="text-xs fill-gray-500">{p.label}</text>
                      </g>
                    ))}
                  </svg>
                </div>
                <p className="text-center text-sm text-gray-500 mt-4">
                  {t('results.scoreIncreased', {
                    from: performanceHistory[0].value,
                    to: performanceHistory[performanceHistory.length - 1].value,
                  })}
                </p>
              </div>
            )}

            {/* Tips */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-amber-900 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5" />
                {t('results.tips.title')}
              </h2>
              <ul className="space-y-2">
                {tips.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-amber-800">
                    <span className="w-5 h-5 rounded-full bg-amber-200 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* Actions */}
            <div className="bg-white rounded-2xl shadow-lg p-4 space-y-3">
              <Button
                variant="primary"
                className="w-full"
                onClick={handleReviewAnswers}
                icon={<Search className="w-4 h-4" />}
                disabled={isSpeaking && isGradingPending}
              >
                {t('results.reviewAnswers')}
              </Button>
              {isSpeaking && isGradingFailed && (
                <>
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={handleRetrySpeakingGrading}
                    loading={retryingGrading}
                    icon={<RotateCcw className="w-4 h-4" />}
                  >
                    {t('results.retrySpeakingGrading')}
                  </Button>
                  {retryError && <p role="alert" className="text-sm text-error">{retryError}</p>}
                </>
              )}
              {isWritingUngraded && (
                <>
                  <Button variant="secondary" className="w-full" onClick={handleGradeWriting} loading={retryingGrading}>
                    {t('results.gradeWriting')}
                  </Button>
                  {retryError && <p role="alert" className="text-sm text-error">{retryError}</p>}
                </>
              )}
              <Button
                variant="secondary"
                className="w-full"
                onClick={handleNewExam}
                icon={<RotateCcw className="w-4 h-4" />}
              >
                {t('results.newExam')}
              </Button>
              <Button
                variant="tertiary"
                className="w-full"
                onClick={() => navigate('/dashboard')}
                icon={<Home className="w-4 h-4" />}
              >
                {t('results.toDashboard')}
              </Button>
            </div>

            {/* Share */}
            <div className="bg-white rounded-2xl shadow-lg p-4 space-y-3">
              <Button
                variant="tertiary"
                className="w-full"
                onClick={handleShare}
                icon={<Share2 className="w-4 h-4" />}
              >
                {t('results.share')}
              </Button>
            </div>

            {/* Exam Details */}
            <div className="bg-white rounded-2xl shadow-lg p-4">
              <h3 className="font-bold text-gray-900 mb-3">{t('results.examDetails.title')}</h3>
              <div className="space-y-2 text-sm">
                {!isKnm && <div className="flex justify-between">
                  <span className="text-gray-500">{t('results.examDetails.program')}</span>
                  <span className="font-medium text-gray-900">{t('examSelection.program')} {program}</span>
                </div>}
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('results.examDetails.section')}</span>
                  <span className="font-medium text-gray-900">{section}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('results.examDetails.totalQuestions')}</span>
                  <span className="font-medium text-gray-900">{totalQuestions}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('results.examDetails.avgTimePerQuestion')}</span>
                  <span className="font-medium text-gray-900">{averageTimePerQuestion}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('results.examDetails.date')}</span>
                  <span className="font-medium text-gray-900">
                    {data?.endTime
                      ? new Date(data.endTime).toLocaleDateString('nl-NL')
                      : new Date().toLocaleDateString('nl-NL')}
                  </span>
                </div>
              </div>
            </div>

            {/* Your score */}
            {!isGradingPending && !isGradingFailed && (
            <div className="bg-primary/5 rounded-2xl p-4">
              <h3 className="font-bold text-primary mb-2">{t('results.score.title')}</h3>
              <p className="text-sm text-gray-600 mb-2">{t('results.score.summary', { percentage: Math.round(overallScore) })}</p>
              <div className="w-full bg-gray-200 h-2 rounded-full">
                <div className="h-2 rounded-full bg-primary" style={{ width: `${Math.min(100, Math.round(overallScore))}%` }} />
              </div>
            </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResultsPage;
